import { StringSearchFilterType } from '@warebee/frontend/data-access-api-graphql';
import classNames from 'classnames';
import _ from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import { JsonObject } from 'type-fest';
import { AsyncLoadStatus } from '../common/types';
import { Button } from '../components/actions/Button';
import DatasetTable, { ColumnConfig } from '../components/DatasetTable';
import { Spacer } from '../components/layout/Spacer';
import { columnDisplayModeState } from '../datasetObject/store/datasetObject.state';
import useLoadCustomQueryData from './hooks/useLoadCustomQueryData';
import {
  customQueryData,
  customQueryDataLoadStatus,
  customQueryDataState,
} from './store/customQuery.state';

export type CustomQueryDataTableProps = {
  queryDescriptorId: string;
  children?: React.ReactNode;
};

const CustomQueryDataTable: React.FC<CustomQueryDataTableProps> = props => {
  const { t } = useTranslation('dataset');
  const data = useRecoilValue(customQueryData(props.queryDescriptorId));
  const status = useRecoilValue(
    customQueryDataLoadStatus(props.queryDescriptorId),
  );
  const [dataState, setDataState] = useRecoilState(
    customQueryDataState(props.queryDescriptorId),
  );
  const [columnDisplayMode, setColumnDisplayMode] = useRecoilState(
    columnDisplayModeState,
  );
  const [loadData, resetData] = useLoadCustomQueryData();

  // Debug the data structure
  // console.log('CustomQueryDataTable data structure:', {
  //   data,
  //   hasContent: !!data?.content,
  //   contentLength: data?.content?.length,
  //   firstRow: data?.content?.[0],
  //   columns: data?.columns,
  // });

  const columnsConfig: ColumnConfig<JsonObject>[] = _.map(
    data.columns,
    column => ({
      field: _.camelCase(column.name),
      title:
        columnDisplayMode === 'formatted'
          ? _.startCase(_.replace(column.name, '_', ' '))
          : column.name,
      hasFilter: true, // Enable filtering for all columns
      rawName: column.name,
    }),
  ) as any;

  // console.log('Query data:', {
  //   columns: data?.columns,
  //   content: data?.content?.slice(0, 3), // First 3 rows for debugging
  //   totalCount: data?.totalCount,
  // });

  useEffect(() => {
    return () => {
      resetData({ queryId: props.queryDescriptorId });
    };
  }, []);

  function callDataLoad(
    searchValues: Record<string, string>,
    page = { isAppend: false, skip: 0 },
  ) {
    if (_.isNil(props.queryDescriptorId)) return;

    const filterBy = _.reduce(
      searchValues,
      (acc, value, key) => {
        if (!value) return acc;
        return {
          ...acc,
          [key]: {
            type: StringSearchFilterType.CONTAINS,
            value: value,
          },
        };
      },
      {},
    );

    loadData({
      queryId: props.queryDescriptorId,
      filterBy,
    });
  }

  function onLoadNext() {
    callDataLoad(dataState.searchValues, {
      isAppend: true,
      skip: itemsCount,
    });
  }

  const isLoading = status === AsyncLoadStatus.Loading;
  const itemsCount = data?.content?.length || 0;
  const totalCount = data?.totalCount ?? 0;

  // Calculate summary statistics from the current data
  const summaryStats = useMemo(() => {
    // console.log('Calculating stats, data:', {
    //   hasColumns: !!data?.columns,
    //   columnsCount: data?.columns?.length,
    //   hasContent: !!data?.content,
    //   contentLength: data?.content?.length,
    // });

    if (!data?.content?.length || !data?.columns) {
      // console.log('No data to calculate stats');
      return null;
    }

    const stats: Record<string, any> = {};

    // Analyze each column
    data.columns.forEach(column => {
      const fieldName = _.camelCase(column.name);
      const allValues = data.content.map(row => row[fieldName]);
      const nonNullValues = allValues.filter(v => v != null && v !== '');
      const blankCount = allValues.length - nonNullValues.length;

      // For distinct count, treat null/empty as one "blank" value
      const distinctValues = new Set(nonNullValues);
      const distinctCount = distinctValues.size + (blankCount > 0 ? 1 : 0);

      // Always create stats even if all values are blank
      const hasAnyValues = allValues.length > 0;
      if (!hasAnyValues) return;

      // For numeric types, calculate sum, min, max, avg
      if (
        ['int', 'integer', 'float', 'double', 'decimal', 'bigint'].includes(
          column.type,
        )
      ) {
        const numericValues = nonNullValues
          .map(v => Number(v))
          .filter(v => !isNaN(v));
        stats[fieldName] = {
          type: 'numeric',
          name: column.name,
          sum: numericValues.length > 0 ? _.sum(numericValues) : 0,
          min: numericValues.length > 0 ? _.min(numericValues) : null,
          max: numericValues.length > 0 ? _.max(numericValues) : null,
          avg: numericValues.length > 0 ? _.mean(numericValues) : null,
          count: allValues.length,
          nonNullCount: nonNullValues.length,
          blankCount,
          distinctCount,
        };
      }
      // For string types, count unique values
      else if (['varchar', 'text', 'string', 'char'].includes(column.type)) {
        stats[fieldName] = {
          type: 'string',
          name: column.name,
          uniqueCount: distinctCount,
          distinctCount, // Add this to be consistent
          count: allValues.length,
          nonNullCount: nonNullValues.length,
          blankCount,
        };
      }
      // For boolean types
      else if (['boolean', 'bool'].includes(column.type)) {
        const trueCount = nonNullValues.filter(
          v => v === true || v === 'true' || v === 1,
        ).length;
        const falseCount = nonNullValues.filter(
          v => v === false || v === 'false' || v === 0,
        ).length;
        stats[fieldName] = {
          type: 'boolean',
          name: column.name,
          trueCount,
          falseCount,
          blankCount,
          truePercent:
            allValues.length > 0 ? (trueCount / allValues.length) * 100 : 0,
          distinctCount,
          count: allValues.length,
          nonNullCount: nonNullValues.length,
        };
      }
      // For date/time types
      else if (['date', 'datetime', 'timestamp'].includes(column.type)) {
        const dates = nonNullValues
          .map(v => new Date(v))
          .filter(d => !isNaN(d.getTime()));
        const earliestDate =
          dates.length > 0
            ? new Date(_.min(dates.map(d => d.getTime())))
            : null;
        const latestDate =
          dates.length > 0
            ? new Date(_.max(dates.map(d => d.getTime())))
            : null;
        const daysBetween =
          earliestDate && latestDate
            ? Math.ceil(
                (latestDate.getTime() - earliestDate.getTime()) /
                  (1000 * 60 * 60 * 24),
              )
            : 0;

        stats[fieldName] = {
          type: 'date',
          name: column.name,
          earliest: earliestDate,
          latest: latestDate,
          daysBetween,
          count: allValues.length,
          nonNullCount: dates.length,
          blankCount,
          distinctCount,
        };
      }
      // For other types, just count distinct values
      else {
        stats[fieldName] = {
          type: 'other',
          name: column.name,
          count: allValues.length,
          nonNullCount: nonNullValues.length,
          blankCount,
          distinctCount,
        };
      }
    });

    // console.log('Calculated stats:', stats);
    return stats;
  }, [data]);

  // Create enhanced field summary with statistics
  const enhancedFieldsSummary = useMemo(() => {
    if (!data?.columns) return [];

    // console.log('Creating enhanced summaries, summaryStats:', summaryStats);

    const summaries = data.columns.map(column => {
      const fieldName = _.camelCase(column.name);
      const stat = summaryStats?.[fieldName];

      // console.log(`Field ${column.name} -> ${fieldName}, stat:`, stat);

      const summary: any = {
        name: fieldName, // Use camelCase name to match column.field
        type: [column.type],
        hasData: true, // Set to true if we have data
      };

      if (stat) {
        // All types get distinct count
        summary.distinctValueCount =
          stat.distinctCount || stat.uniqueCount || 0;

        // Add blank count if present
        if (stat.blankCount > 0) {
          summary.valueCounters = summary.valueCounters || {};
          summary.valueCounters[t`[Blank]`] = stat.blankCount;
        }

        if (stat.type === 'numeric') {
          summary.numberRange =
            stat.min !== null && stat.max !== null
              ? [stat.min, stat.max]
              : null;
          summary.totalSum = stat.sum;
        } else if (stat.type === 'boolean') {
          summary.valueCounters = {
            ...summary.valueCounters,
            true: stat.trueCount,
            false: stat.falseCount,
          };
        } else if (stat.type === 'date' && stat.earliest && stat.latest) {
          // Add date-specific information to valueCounters
          summary.valueCounters = {
            ...summary.valueCounters,
            [`First: ${stat.earliest.toLocaleDateString()}`]: 1,
            [`Last: ${stat.latest.toLocaleDateString()}`]: 1,
            [`Range: ${stat.daysBetween} days`]: 1,
          };
        }
      } else {
        // If no stat, still try to provide basic info
        summary.distinctValueCount = 0;
      }

      return summary;
    });

    // console.log('Enhanced field summaries:', summaries);
    return summaries;
  }, [data, summaryStats]);

  // async function startExportCSV() {
  //   return await exportCSV({
  //     datasetId: props.queryDescriptorId,
  //     columnsConfig,
  //   });
  // }

  return (
    <DatasetTable
      id={`custom-query-results-${props.queryDescriptorId}`}
      data={data?.content}
      columnsConfig={columnsConfig}
      keyFields={['eventId']}
      hasCounter
      subtitle={t`Datasets`}
      isSticky
      fieldsSummary={enhancedFieldsSummary}
      hasRowCounter
      onLoadNext={onLoadNext}
      onSearch={v => {
        const transformedValues = _.mapKeys(v, (value, key) =>
          _.camelCase(key),
        );
        setDataState({ ...dataState, searchValues: transformedValues });
        // Only load data when search is explicitly triggered
        callDataLoad(transformedValues);
      }}
      totalCount={totalCount}
      searchValues={dataState.searchValues}
      isLoading={isLoading}
      // onStartExportClick={startExportCSV}
      actionSection={
        <>
          <Button
            className={classNames(
              'hover:text-menu-active',
              'bg-menu/50 hover:bg-menu',
              'text-xxs',
              'flex items-center',
              'rounded-full',
              'px-2 py-1',
              'focus:outline-none focus:ring-0',
            )}
            buttonSize="xs"
            hasIconAfter
            label={columnDisplayMode === 'formatted' ? t`Raw` : t`Formatted`}
            onPress={() =>
              setColumnDisplayMode(mode =>
                mode === 'formatted' ? 'raw' : 'formatted',
              )
            }
          />

          <Spacer flexspace />
        </>
      }
    />
  );
};

export default CustomQueryDataTable;
